import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { SessionUser } from '@shared/schemas';
import { ApiFailure, api, setActiveOrg, setUnauthorizedHandler } from '@/lib/api';

/**
 * Who is signed in, and which org they are acting as.
 *
 * The server is the authority — it verifies org membership on every request — so
 * this holds no permissions of its own. It exists so the UI can render the right
 * shell without a round trip on every navigation.
 */

interface SessionState {
  user: SessionUser | null;
  loading: boolean;
  orgId: string | null;
  refresh: () => Promise<void>;
  switchOrg: (orgId: string) => void;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

const ORG_STORAGE_KEY = 'mpp.activeOrg';

function readStoredOrg(): string | null {
  try {
    return localStorage.getItem(ORG_STORAGE_KEY);
  } catch {
    // Private windows and blocked site data both throw; a remembered team is a
    // convenience, never a requirement.
    return null;
  }
}

function storeOrg(orgId: string | null) {
  try {
    if (orgId) localStorage.setItem(ORG_STORAGE_KEY, orgId);
    else localStorage.removeItem(ORG_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<SessionUser>('/auth/me');
      setUser(me);
      const stored = readStoredOrg();
      const valid = me.orgs.find((o) => o.id === stored)?.id;
      const next = valid ?? me.orgs[0]?.id ?? null;
      setOrgId(next);
      setActiveOrg(next);
      if (next !== stored) storeOrg(next);
    } catch (err) {
      if (err instanceof ApiFailure && err.status === 401) {
        setUser(null);
        setOrgId(null);
        setActiveOrg(null);
      } else {
        throw err;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
    A session that ends while the app is open ends here too.

    The server is the authority, and when it stops recognising the session every
    query starts failing — but the shell went on rendering a signed-in app full
    of error panels, with nothing anywhere telling the coach to sign in again.
    Dropping the user sends RequireSession to the sign-in page, which remembers
    the screen they were on.
  */
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setOrgId(null);
      setActiveOrg(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const switchOrg = useCallback((next: string) => {
    setOrgId(next);
    setActiveOrg(next);
    storeOrg(next);
  }, []);

  const signOut = useCallback(async () => {
    await api.post('/auth/logout');
    setUser(null);
    setOrgId(null);
    setActiveOrg(null);
    storeOrg(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, orgId, refresh, switchOrg, signOut }),
    [user, loading, orgId, refresh, switchOrg, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession used outside SessionProvider');
  return ctx;
}
